import { store } from '@/store'
import { userApi } from '@/store/services'
import { useEffect, useState } from 'react'
import { createSearchParams } from 'react-router-dom'
import { AsyncPaginate } from 'react-select-async-paginate'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { SelectWrapper } from '../wrapper'

const userOption = (user = {}) => ({
  value: user.id,
  label: user.name || [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(' ').trim() || user.email,
  resource: user
})

export const UserSelector = ({
  value,
  onChange,
  label,
  role,
  active = null,
  isMulti = false,
  isClearable = false,
  perPage = 10,
  required = false
}) => {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState(null)

  const loadOptions = async (search, _loadedOptions, { page }) => {
    const searchParams = createSearchParams({
      page,
      per_page: perPage
    })

    if (search) searchParams.set('q', search)
    if (role) searchParams.set('role', role)
    if (active !== null) searchParams.set('active', String(active))

    const { users, meta } = await store.dispatch(
      userApi.endpoints.getUsers.initiate(`?${searchParams.toString()}`, { subscribe: true })
    ).unwrap()

    const totalPages = meta?.pagination?.total_pages || 1

    return {
      options: users.map(userOption),
      hasMore: page < totalPages,
      additional: {
        page: page + 1
      }
    }
  }

  const getCurrentUser = async (id) => {
    const { user = {} } = await store.dispatch(
      userApi.endpoints.getUser.initiate(id)
    ).unwrap()

    return user.id ? userOption(user) : null
  }

  useEffect(() => {
    const fetchCurrentUsers = async () => {
      if (isMulti) {
        const ids = Array.isArray(value) ? value.filter(Boolean) : []

        if (ids.length === 0) {
          setSelectedOption([])
          return
        }

        const selectedIds = (selectedOption || []).map((option) => option.value)
        if (ids.length === selectedIds.length && ids.every((id) => selectedIds.includes(id))) return

        const options = await Promise.all(ids.map((id) => getCurrentUser(id)))
        setSelectedOption(options.filter(Boolean))
        return
      }

      if (!value) {
        setSelectedOption(null)
        return
      }
      if (value === selectedOption?.value) return

      setSelectedOption(await getCurrentUser(value))
    }

    fetchCurrentUsers()
  }, [value, isMulti])

  const handleChange = (option) => {
    if (isMulti) {
      setSelectedOption(option || [])
      onChange(option || [])
      return
    }

    setSelectedOption(option)
    onChange(option)
  }

  return (
    <SelectWrapper label={label} required={required}>
      {(styles) => (
        <AsyncPaginate
          isMulti={isMulti}
          isClearable={isClearable}
          loadingMessage={() => t('select.loading')}
          value={isMulti ? selectedOption || [] : selectedOption?.value ? selectedOption : null}
          debounceTimeout={600}
          placeholder={t('select.one')}
          onChange={handleChange}
          loadOptions={loadOptions}
          styles={styles}
          menuPortalTarget={document.body}
          additional={{ page: 1 }}
        />
      )}
    </SelectWrapper>
  )
}

UserSelector.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  role: PropTypes.string,
  active: PropTypes.bool,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  perPage: PropTypes.number,
  required: PropTypes.bool
}
